/* KANON `Detal klienta v3`, variant a — the summary column, 360px flat and Przegląd's alone.
 *
 * Four cards inside a 14px radius, on the card surface with a hairline and the shared elevation.
 * The identity that used to head this column is in the top bar since v3; see `ClientDetailScreen`.
 *
 * ── The vertical rhythm is TIGHTER than v3's, and it is a fit decision ────────────────────────
 * Owner, 1 September 2026: "postaraj się tak to ścisnąć proszę, żeby się jednak mieściło na ekranie
 * laptopa, ta lewa strona bez scrolla." The column stood at 758px of content, which fits a 900px
 * window exactly and overflows a 800px one by 100. Nothing was removed and no type was shrunk —
 * the typography is calibrated and the owner spent several rounds on it. What moved is space:
 * the gap between cards 18 → 12, the padding inside each card 15 → 12 on the block axis, the gap
 * between a card's own rows 10 → 8, and the plan cover's height (in `shared/ui/PlanCard`).
 *
 * The measured figures are in the round's report. Fit-or-scroll is untouched: `overflow-y: auto`
 * below is the path a 620px window still needs and still has. */

.column {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-3);
  width: var(--w-detail-summary);
  min-height: 0;
  padding-right: var(--space-2px);
  overflow-y: auto;
  scrollbar-gutter: stable;
}

/* ── the card ──────────────────────────────────────────────────────────────── */

/* The column's card frame. It used to be `.card, .planCard { … }`, and the plan card's half of that
 * selector went to `shared/ui/PlanCard` on 1 September 2026 — leaving `.card` behind with no
 * declarations at all for one commit, which took the background, the hairline, the radius and the
 * elevation off the note, the attendance and the goal cards. Owner: "nagle karty przestały być
 * kartami." Restored verbatim; the tokens are the ones they always had. */
.card {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  padding: var(--space-15px) var(--space-4);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

/* v3 draws the note card without the elevation: it is the quietest thing in the column, and a
 * shadow under a block of the trainer's own prose reads as a control. */
.cardQuiet {
  box-shadow: none;
}

/* ── A card that cannot draw its content yet still holds its place ────────────────────────────
 * Three of these four cards have a state that is one sentence long — the read is open, it failed,
 * or it came back empty — and the loaded state is 90 to 100 pixels taller. Without a floor the
 * column re-flows the moment each response lands, and the cards below travel: measured on the
 * running screen on 1 September 2026, the goal card moved 191px in a single frame and the plan
 * card swapped one component for another 76px taller. That is what the owner sees as a jump.
 *
 * The reserve is on the SHORT states rather than on `.card` itself, so a loaded card is sized by
 * its content exactly as before, and it is `min-height` so a card with more to say still grows.
 * The note card is not in the list: it animates its own height, and it is the one card whose
 * content the trainer changes deliberately — see the component. */
.reserveAttendance {
  min-height: var(--h-summary-attendance);
}

.reservePlan {
  min-height: var(--h-summary-plan);
}

.reserveGoal {
  min-height: var(--h-summary-goal);
}

/* The self-paced client's card stands in the frekwencja card's slot and answers the same question,
 * so it holds the same box. */
.reserveStanding {
  min-height: var(--h-summary-attendance);
}

.cardHead,
.cardFoot {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

/* "Tylko dla Ciebie" is v3's `privNote`: 10px at 600, a step quieter than the label beside it. */
.cardAside {
  flex: none;
  font-size: var(--fs-badge);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

.cardNotice {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.cardHint {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.error {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--danger-text-strong);
}

.footNote {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* v3's `tinyBtn`: 26px, 11.5px at 700, on the card fill with a `--border-2` edge.
 *
 * GEOMETRY ONLY, plus the one state this column's smaller ghost has that the tabs' does not. The
 * treatment is `shared/ui/ghostButton.module.css`, worn as a second class, and nothing may be
 * declared on both sides — see that file. */
.ghost {
  height: var(--h-detail-tiny-btn);
  padding: 0 var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
  border-radius: var(--radius-8px);
}

.ghost:hover:not(:disabled) {
  color: var(--text);
}

/* Geometry only — the fill and the states come from `shared/ui/inkButton`. */
.primarySm {
  height: var(--h-detail-tiny-btn);
  padding: 0 var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: normal;
  border-radius: var(--radius-8px);
}

/* ── the note ──────────────────────────────────────────────────────────────── *
 * Plain type on the card. v3 draws no box around the read state; the card's own "Tylko dla Ciebie"
 * line says what a dashed outline used to, in words, and the EDIT state is still a real field. */

.noteBody {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
  white-space: pre-wrap;
}

.noteEmpty {
  font-style: italic;
  color: var(--text-3);
}

.noteField {
  min-height: var(--h-detail-note);
  padding: var(--space-11px) var(--space-3);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text);
  resize: vertical;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-11px);
}

.noteField:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-1px);
}

/* ── frekwencja ────────────────────────────────────────────────────────────── *
 * v3's shape: the fortnight as a 32px figure with its sentence beside it, the month as twenty
 * session cells, the month's own sentence underneath. */

.attHero {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  margin: var(--space-0);
}

.attFigure {
  font-size: var(--fs-attendance);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text);
  letter-spacing: var(--ls-panel-figure);
  font-variant-numeric: tabular-nums;
}

.attHeroText {
  font-size: var(--fs-client-cell);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* The strip itself is `shared/ui/TrainingWeekStrip` — the roster preview panel's, promoted on this
 * card as its second consumer. Nothing about the marks, the captions or the legend is styled here,
 * deliberately: a copy kept beside the shared one is a copy that wins in dev and loses in the build.
 *
 * What IS here is the line under the hero — „2 opuszczone · ostatni trening wczoraj" (KANON „Karty
 * zadań" `.a-att-sub`, 24 September 2026). This line, the plan window and the goal labels are
 * `--text-2` where the canon writes `--text-3` (3.0:1) — the deviation `DetailTab.module.css` records. */
.attSub {
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

/* ── standing (the self-paced client's card) ───────────────────────────────── */

.standingLead {
  margin: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-snug);
  color: var(--text);
}

.standingLine,
.standingWindow {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.standingBalance {
  display: flex;
  gap: var(--space-6);
  margin: var(--space-0);
}

.standingFigure {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
}

.standingFigureLabel {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
}

.standingFigureValue {
  margin: var(--space-0);
  font-size: var(--fs-roster-kpi);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text);
  letter-spacing: var(--ls-title);
  font-variant-numeric: tabular-nums;
}

/* The plan card's own declarations left with it, to `shared/ui/PlanCard` — including the hover and
 * focus treatment, which now belong to the whole card rather than to a link inside it (owner,
 * 2 September 2026: the card IS the link). What was left behind here was three dead selectors, and
 * one of them had swallowed the goal's: `.planEnds,` sat immediately above `.goalActions` with only
 * a comment between them, so the two were one rule. */

/* ── the goal ──────────────────────────────────────────────────────────────── */

.goalRow {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
  min-width: 0;
}

.goalTitle {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-goal-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-section);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* start · teraz · cel — KANON „Karty zadań" `.a-gfigs`: three equal cells on the inset ground,
 * divided by hairlines, the middle one emphasised. */
.goalFigures {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: var(--space-0);
  background: var(--bg-inset);
  border-radius: var(--radius-12px);
}

.goalFigure {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
  padding: var(--space-9px) var(--space-3);
}

.goalFigure + .goalFigure {
  border-inline-start: var(--border-hairline) solid var(--border);
}

.goalValue {
  margin: var(--space-0);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  letter-spacing: var(--ls-heading);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.goalFigureNow .goalValue {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.goalLabel {
  font-size: var(--fs-goal-lab);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
}

.goalUnmeasured {
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

/* The note card's changing half. Its height is animated by Motion — see `NoteCard` — and the two
 * states it travels between are a one-line paragraph and a four-row field.
 *
 * The flex column here reproduces the card's own gap inside the animated box: the box is a single
 * child of `.card`, so the paragraph and the foot would otherwise sit against each other.
 * `overflow: hidden` is what makes the travel read as a card growing rather than as its contents
 * spilling past the border mid-transition. */
.noteSlot {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  overflow: hidden;
}

/* ── The column's four skeletons ───────────────────────────────────────────────
 *
 * The treatment — the fill, the radius and the slow neutral sheen — is `shared/ui/skeleton`'s;
 * everything below is geometry, which is the half a shared module deliberately does not own (the
 * same split `photoBox` states). Every bar stands where a real element will and is as tall as the
 * type it replaces, so a card fills its reserve exactly and the answer lands INTO the shape rather
 * than under it.
 *
 * None of these is drawn during the first `SKELETON_GRACE_MS` of a wait — see
 * `shared/lib/loadingGate`. The BOX is the card's reserve in that window too, so the bars appearing
 * is a change of ink and never of geometry. */

.skelLead {
  width: 68%;
  height: var(--fs-body);
}

.skelLine {
  width: 84%;
  height: var(--fs-micro);
}

.skelLineShort {
  width: 52%;
  height: var(--fs-micro);
}

/* The note's one line, and the box it must occupy before there is a bar in it. `min-height` on the
 * paragraph rather than a height on the bar: the paragraph is what the animated slot measures, and
 * during the grace window it holds no bar at all. */
.noteLineReserve {
  min-height: calc(var(--fs-note) * var(--lh-relaxed));
}

.skelNoteLine {
  display: block;
  width: 76%;
  height: var(--fs-note);
}

/* Frekwencja: the hero figure, the strip's caption, its fourteen marks and the legend line. */
.skelHero {
  width: 58%;
  height: var(--fs-attendance);
}

.skelCaption {
  width: 38%;
  height: var(--fs-micro-label);
}

.skelStrip {
  display: grid;
  grid-template-columns: repeat(14, 1fr);
  gap: var(--space-5px);
}

/* The strip's own mark: 26px tall on a 5px radius, `shared/ui/TrainingWeekStrip`'s `.mark`. */
.skelCell {
  height: 26px;
  border-radius: var(--radius-5px);
}

.skelLegend {
  width: 92%;
  height: var(--fs-caption);
}

/* The plan card's cover, at the height the photograph's box holds in every state. */
.skelCover {
  width: 100%;
  height: var(--h-plan-cover);
  border-radius: var(--radius-sm);
}

/* The goal card's ring and the three lines beside it. */
.skelGoalRow {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.skelRing {
  flex: none;
  width: var(--sz-goal-ring);
  height: var(--sz-goal-ring);
}

.skelGoalText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/* An empty answer sits in the middle of the box its card reserved. A skeleton does not: it is the
 * content's own shape and belongs where the content will be. */

/* An empty card keeps its eyebrow where a loaded one has it — at the top, in line with the eyebrows
 * of the cards above — and centres only what is left.
 *
 * `justify-content: center` on the card itself was the previous spelling, and it centred the eyebrow
 * WITH the answer: on a reserved card that is ~110px of blank above the label, so "CEL COACHINGOWY" sat
 * mid-card while "FREKWENCJA" and "AKTYWNY PLAN" sat at the top of theirs. The owner read it as the
 * card being broken (2 September 2026).
 *
 * Growing the answer instead leaves the reserve's slack below the text, which is what a held box is
 * supposed to look like, and costs nothing: `min-height` still floors the card, so nothing under it
 * moves when the read lands. */
.centred > :last-child {
  flex: 1;
  align-content: center;
}
